import React, { ForwardRefExoticComponent, useEffect, useState } from 'react';
import BEMHelper from '@ynap/bem-helper';
import { redirectToWishlist } from '@ynap/redirect-utils';
import { useConfig } from '@ynap/immutables-provider';
import { getItemFromWebStorage } from '@ynap/web-storage-utils';
import { withOptimizely } from '@ynap/optimizely-utils';
import { notificationActions } from '@ynap/notification-state';
import { useDispatch } from 'react-redux';
import { IconAnimated } from '@ynap/animations';
import { constructUrl } from '@ynap/url-configuration';
import { safeBuildEventObj, withAnalytics } from '@ynap/analytics';
import { raygun } from '@ynap/clientside-monitoring';
import { trackAddToWishlist } from '../analytics';
import { useTracking } from 'react-tracking';
import SizeSelectModal from './SizeSelectModal/SizeSelectModal';
import LoginAndRegistrationModal from './LoginAndRegistrationModal/LoginAndRegistrationModal';
import SuccessModal from './SuccessModal/SuccessModal';

const iframeThreshold = 10;

const bem = new BEMHelper('AddWishlistButton');

const ANIMATION_STATES = {
    stroke: 'stroke',
    filledMask: 'filledMask',
} as const;

const ANIMATION_LOADING_TIME = 600;
const ANIMATION_FILLEDMASK_TIME = 2100;
const ANIMATION_END = 6000;

const WISHLIST_MODALS = {
    loginAndRegistration: 'loginAndRegistration',
    sizeSelect: 'sizeSelect',
    success: 'success',
} as const;

type AnimationState = (typeof ANIMATION_STATES)[keyof typeof ANIMATION_STATES];
type ModalType = (typeof WISHLIST_MODALS)[keyof typeof WISHLIST_MODALS];

type AddWishlistButtonProps = {
    product: any;
    locale: string;
    messages: any;
    wishlist: any;
    optimizely: any;
    analytics?: any;
};

const AddWishlistButton = (props: AddWishlistButtonProps) => {
    const { product, locale, messages, wishlist, optimizely, analytics } = props;

    const [modalOpen, setModalOpen] = useState<ModalType | null>(null);
    const [inWishList, setInWishList] = useState(false);
    const [animationState, setAnimationState] = useState<AnimationState | null>(null);
    const [iconDisabledState, setIconDisabledState] = useState(false);
    const [userUberToken, setUserUberToken] = useState<string | null>(null);
    const [iframeHeight, setIframeHeight] = useState(0);
    const [selectedSku, setSelectedSku] = useState<object | null>(null);

    const dispatch = useDispatch();
    const { trackEvent } = useTracking();
    const config = useConfig();

    const urlConfig = config?.global?.urlConfig;
    const hasAnimation = config?.head?.plp?.addWishListAnimation;
    const brand = config?.global?.application?.brandId;
    const signInIframeUrl = config?.head?.signInIframeUrl;

    const isLoginAndRegistrationModalOpen = modalOpen === WISHLIST_MODALS['loginAndRegistration'];
    const isSizeSelectModalOpen = modalOpen === WISHLIST_MODALS['sizeSelect'];
    const isSuccessfulAddedItemModalOpen = modalOpen === WISHLIST_MODALS['success'];

    const handleAddToWishlist = async (sizeSku: any, closeModal: () => void) => {
        optimizely?.track?.('wl_sessionsadd');

        const userSummary = await (window as any)?.SF?.getUserSummary();

        if (!userUberToken && !userSummary) {
            setSelectedSku(sizeSku);
            setModalOpen(WISHLIST_MODALS.loginAndRegistration);
            return;
        }

        try {
            const response = await wishlist.addToWishList(product.partNumber, sizeSku);
            const productAnalytics = analytics?.listing?.item?.find((item) => item.productInfo?.productID === product?.partNumber);
            const category = analytics?.page?.category;

            if (response.success) {
                closeModal();
                setInWishList(true);
                trackEvent(
                    safeBuildEventObj(trackAddToWishlist, productAnalytics, sizeSku?.partNumber, sizeSku?.size?.labelSize, sizeSku?.size?.schemas, category),
                );
                return;
            }

            if (response.error) {
                const errorCode = response.error.errors?.[0]?.errorCode || 'UNKNOWN';

                setIconDisabledState(false);
                closeModal();

                return dispatch(
                    notificationActions.setNotification({
                        name: 'action',
                        type: 'popup-error',
                        message: messages.notifications.errors({ error: errorCode }),
                    }),
                );
            }
        } catch (error) {
            raygun.helpers.agent('send', {
                error,
                tags: ['PLP', 'AddToWishlist'],
            });

            if (`${error}`.includes('uberToken')) {
                setSelectedSku(sizeSku);
                setModalOpen(WISHLIST_MODALS.loginAndRegistration);
                return;
            }

            setIconDisabledState(false);
            closeModal();

            return dispatch(
                notificationActions.setNotification({
                    name: 'action',
                    type: 'popup-error',
                    message: messages.notifications.errors({ error: 'UNKNOWN' }),
                }),
            );
        }
    };

    const handleCloseModal = () => setModalOpen(null);

    useEffect(() => {
        const handleIframeInfo = (event: MessageEvent) => {
            const { type, height, uberToken } = event.data || {};
            const iframeHeightWithThreshold = height + iframeThreshold;

            if (type === 'accountModalHeight' && iframeHeight !== iframeHeightWithThreshold) {
                setIframeHeight(iframeHeightWithThreshold);
            }

            if (type === 'updateUbertoken' && uberToken && !userUberToken) {
                setUserUberToken(uberToken);
            }
        };

        window.addEventListener('message', (event) => handleIframeInfo(event));
        return () => window.removeEventListener('message', (event) => handleIframeInfo(event));
    }, []);

    useEffect(() => {
        if (userUberToken && selectedSku) {
            (window as any).SF.notifyUserChange({ uberToken: userUberToken });
            handleAddToWishlist(selectedSku, handleCloseModal);
        }
    }, [userUberToken]);

    //Animation
    useEffect(() => {
        if (inWishList) {
            setAnimationState(ANIMATION_STATES['stroke']);

            const strokeTimeout = setTimeout(() => {
                setAnimationState(ANIMATION_STATES['filledMask']);
            }, ANIMATION_LOADING_TIME);

            const successMessageShown = getItemFromWebStorage('sessionStorage', 'AWLsuccessMessage');
            let openSuccessModalTimeout: NodeJS.Timeout;

            if (!successMessageShown) {
                openSuccessModalTimeout = setTimeout(() => {
                    setModalOpen(WISHLIST_MODALS.success);
                }, ANIMATION_FILLEDMASK_TIME);
            }

            const resetAnimation = setTimeout(() => {
                if (successMessageShown) {
                    setAnimationState(null);
                    setInWishList(false);
                    setIconDisabledState(false);
                }
            }, ANIMATION_END);

            return () => {
                clearInterval(strokeTimeout);
                clearInterval(openSuccessModalTimeout);
                clearInterval(resetAnimation);
            };
        }
    }, [inWishList]);

    // Reset animation after success message is closed
    useEffect(() => {
        if (!isSuccessfulAddedItemModalOpen && inWishList) {
            const resetAnimation = setTimeout(() => {
                const successMessageShown = getItemFromWebStorage('sessionStorage', 'AWLsuccessMessage');
                if (successMessageShown) {
                    setAnimationState(null);
                    setInWishList(false);
                    setIconDisabledState(false);
                }
            }, ANIMATION_END / 3);

            return () => {
                clearInterval(resetAnimation);
            };
        }
    }, [isSuccessfulAddedItemModalOpen]);

    const handleClick = () => {
        setModalOpen(WISHLIST_MODALS.sizeSelect);
        setIconDisabledState(true);

        optimizely?.track('plp_clickonwl');
    };

    const goToWishlist = () => {
        optimizely?.track('wl_sessionsview');
        redirectToWishlist(urlConfig, '', locale);
    };

    const renderIcon = () => {
        return (
            <div className={bem('contentIconAnimation')}>
                <IconAnimated
                    style={{ height: '20px', width: '20px' }}
                    icon={animationState}
                    loop={false}
                    brand={brand}
                    fallbackIcon={<div className={bem('contentIcon', { disabled: true })} />}
                />
            </div>
        );
    };

    const handlePendingItemModalClose = () => {
        handleCloseModal();
        setIconDisabledState(false);
    };

    const handleSuccessModalClose = () => {
        handleCloseModal();
        setInWishList(true);
    };

    if (product?.notStockedOnline) return null;

    return (
        <>
            {isLoginAndRegistrationModalOpen && (
                <LoginAndRegistrationModal
                    iframeHeight={iframeHeight}
                    onCloseModal={handlePendingItemModalClose}
                    signInIframeUrl={constructUrl(signInIframeUrl, { locale })}
                />
            )}
            {isSizeSelectModalOpen && (
                <SizeSelectModal
                    product={product}
                    wishlist={wishlist}
                    messages={messages}
                    optimizely={optimizely}
                    onAddToWishlist={handleAddToWishlist}
                    onCloseModal={handleCloseModal}
                    onPendingItemCloseModal={handlePendingItemModalClose}
                />
            )}
            {isSuccessfulAddedItemModalOpen && <SuccessModal messages={messages} onCloseModal={handleSuccessModalClose} goToWishlist={goToWishlist} />}
            <div className={bem('addWishlistButton')}>
                {hasAnimation && inWishList ? (
                    <div onClick={handleClick}>{renderIcon()}</div>
                ) : (
                    <div
                        data-testid="addWishlistButtonIcon"
                        className={bem('contentIcon', { added: inWishList, disabled: iconDisabledState })}
                        onClick={handleClick}
                    />
                )}
            </div>
        </>
    );
};

// TODO: withOptimizely needs typing
export default withAnalytics(withOptimizely(AddWishlistButton) as ForwardRefExoticComponent<AddWishlistButtonProps>);
